import { Wifi, WifiOff, RefreshCw, AlertTriangle, Smartphone } from "lucide-react";
import { Crest, StatusBadge, DemoTag } from "./kit";
import { fmtClock, label } from "../lib/api";

export const ConnBadge = ({ conn, queue }) => {
  const map = { online: ["Online", "bg-[#35865C]", Wifi], offline: ["Offline", "bg-[#F02030]", WifiOff], syncing: ["Syncing", "bg-[#343A40]", RefreshCw], action: ["Action required", "bg-[#FFD400] !text-[#171B20]", AlertTriangle], reconnecting: ["Reconnecting", "bg-[#FFD400] !text-[#17191C]", RefreshCw], connecting: ["Connecting", "bg-[#6C757D]", RefreshCw] };
  const [txt, cls, Icon] = map[conn] || map.connecting;
  return (
    <span data-testid="connectivity-status" className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold text-white ${cls}`}>
      <Icon className="h-3.5 w-3.5" />{txt}{queue?.length ? ` · ${queue.length}` : ""}
    </span>
  );
};

export const DeviceBadge = ({ device }) => (
  <span data-testid="device-connection-status" className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${device ? "bg-[#171B20] text-white" : "bg-[#E7EAEE] text-[#6C757D]"}`}>
    <Smartphone className="h-3.5 w-3.5" />{device ? "TRO One" : "No device"}
  </span>
);

export function TacticalHeader({ match, elapsed, conn, queue, device }) {
  const added = match.clock?.added?.[match.status];
  return (
    <div data-testid="match-header" className="tro-card overflow-hidden">
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 px-3 py-2 md:px-5 md:py-3">
        <div className="flex min-w-0 items-center justify-end gap-2 text-right"><span className="truncate text-sm font-extrabold md:text-lg"><span className="md:hidden">{match.home.short}</span><span className="hidden md:inline">{match.home.name}</span></span><Crest club={match.home} size={34} /></div>
        <div className="text-center">
          <div data-testid="official-score" className="font-mono text-3xl font-extrabold leading-none tracking-tight md:text-4xl">{match.score.home}<span className="text-[#C5CBD1]">:</span>{match.score.away}</div>
          <div className="mt-1 flex items-center justify-center gap-1">
            <span data-testid="match-clock" className="tro-clock rounded-md bg-[#17191C] px-2 py-0.5 text-base font-bold text-white">{fmtClock(elapsed)}</span>
            {added ? <span data-testid="added-time" className="rounded-md bg-[#E6E9ED] px-1.5 py-0.5 font-mono text-xs font-bold">+{added}'</span> : null}
          </div>
        </div>
        <div className="flex min-w-0 items-center gap-2"><Crest club={match.away} size={34} /><span className="truncate text-sm font-extrabold md:text-lg"><span className="md:hidden">{match.away.short}</span><span className="hidden md:inline">{match.away.name}</span></span></div>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-1.5 border-t border-[#E6E9ED] bg-[#F7F8F9] px-2 py-1.5">
        <span className="text-[10px] font-bold uppercase tracking-widest text-[#6C757D]" data-testid="match-period">{match.clock?.running ? "Clock running" : "Clock paused"}</span>
        <StatusBadge status={match.status} testid="match-status" />{device !== undefined && <DeviceBadge device={device} />}{conn && <ConnBadge conn={conn} queue={queue} />}
      </div>
    </div>
  );
}

export function MatchHeader({ match, elapsed, conn, queue, compact, device }) {
  const added = match.clock?.added?.[match.status];
  return (
    <div data-testid="match-header" className="tro-card overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#E6E9ED] bg-[#F7F8F9] px-4 py-2 text-xs">
        <div className="flex items-center gap-2 font-semibold text-[#343A40]">
          <span>{match.competition?.name}</span><span className="text-[#C5CBD1]">/</span>
          <span className="font-mono" data-testid="match-number">{match.match_number}</span>
          {match.demo && <span className="hidden sm:inline"><DemoTag /></span>}
        </div>
        <div className="flex items-center gap-2"><StatusBadge status={match.status} testid="match-status" />{device !== undefined && <DeviceBadge device={device} />}{conn && <ConnBadge conn={conn} queue={queue} />}</div>
      </div>
      <div className={`grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 px-4 ${compact ? "py-3" : "py-5"}`}>
        <div className="flex min-w-0 items-center justify-end gap-3 text-right">
          <div className="min-w-0"><div className="truncate text-base font-extrabold md:text-xl"><span className="sm:hidden">{match.home.short}</span><span className="hidden sm:inline">{match.home.name}</span></div><div className="tro-label">Home</div></div>
          <Crest club={match.home} size={compact ? 40 : 52} />
        </div>
        <div className="text-center">
          <div data-testid="official-score" className="font-mono text-4xl font-extrabold tracking-tight md:text-5xl">{match.score.home}<span className="text-[#C5CBD1]">:</span>{match.score.away}</div>
          <div className="mt-1 flex items-center justify-center gap-2">
            <span data-testid="match-clock" className="tro-clock rounded-md bg-[#17191C] px-2.5 py-1 text-lg font-bold text-white">{fmtClock(elapsed)}</span>
            {added ? <span data-testid="added-time" className="rounded-md bg-[#E6E9ED] px-2 py-1 font-mono text-sm font-bold">+{added}'</span> : null}
          </div>
          <div className="mt-1 text-[11px] font-bold uppercase tracking-widest text-[#6C757D]" data-testid="match-period">
            {label(match.clock?.period || match.status)}{match.clock?.running ? "" : match.status.includes("half") && !match.status.includes("time") ? " · paused" : ""}
          </div>
        </div>
        <div className="flex min-w-0 items-center gap-3">
          <Crest club={match.away} size={compact ? 40 : 52} />
          <div className="min-w-0"><div className="truncate text-base font-extrabold md:text-xl"><span className="sm:hidden">{match.away.short}</span><span className="hidden sm:inline">{match.away.name}</span></div><div className="tro-label">Away</div></div>
        </div>
      </div>
    </div>
  );
}
